import { MapBanner } from "./components/mapBanner";
import { Fragment, useEffect, useState } from "react";
import ContactTile from "./components/contactTile";
import { InputField } from "../../components/_input/_input";
import { TextArea } from "../../components/_input/_textArea";
import Breadcrumb from "../../components/__simple/breadcrumb";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { changeIcons } from "../../_redux/slices/navSlice";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Joi from "joi";
import { GetMessages, formValidate } from '../../utils/validation';
import config from '../../configuration/config';
import PhoneDropDown from "../../components/_input/_phoneDropDown";
import { toast } from "react-toastify";
import { DropDownInput } from "../../components/_input/_dropdown";
import { Helmet } from "react-helmet";

// Only show map markers for these two cities. Matched against branch
// `content` (localized label) and `name` (id) so it works for AR and EN.
const ALLOWED_BRANCH_KEYWORDS = ["الخبر", "الدمام", "khobar", "dammam", "khubar", "al khobar", "al dammam"];
function isAllowedBranch(b) {
    const haystack = `${b?.name || ""} ${b?.content || ""}`.toLowerCase();
    return ALLOWED_BRANCH_KEYWORDS.some((k) => haystack.includes(k.toLowerCase()));
}

export function ContactPage() {
    const { t: trans, i18n } = useTranslation(["common", "form", "messages", "inputs"]);
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [paramSubject] = useState(query.get("subject") ?? null);

    const dispatch = useDispatch();
    let lang = (i18n.language || 'ar');
    const languageDir = 'language-' + lang;
    const [page, setPage] = useState({});
    const [branches, setBranches] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [message, setMessage] = useState({
        fullName: '',
        phone: '',
        email: '',
        subject: paramSubject,
        messageContent: ''
    });

    const schema = Joi.object({
        fullName: Joi.string().required().messages(GetMessages()),
        phone: Joi.string().min(12).required().messages(GetMessages()),
        email: Joi.string().email({ minDomainSegments: 2, tlds: { allow: false } }).required().messages(GetMessages()),
        subject: Joi.string().required().messages(GetMessages()),
        messageContent: Joi.string().required().messages(GetMessages())
    });

    const [error, setError] = useState({});

    const submitRequest = async (e) => {
        e.preventDefault();
        const result = formValidate(schema, message) ?? {};
        setError(result);
        try {
            if (Object.keys(result).length === 0) {
                console.log(Object.keys(error).length);
                const response = await axios.post(
                    `${config.controllURL}${lang}/api/Pages/AddsiteContactMessages`,
                    {
                        "name": message.fullName,
                        "email": message.email,
                        "phoneNumber": message.phone,
                        "subject": Number(message.subject),
                        "message": message.messageContent,
                    }
                );
                if (response.status === 200 || response.status === 201) {
                    const defaultValue = { ...message };
                    setMessage({
                        fullName: '',
                        phone: defaultValue.phone,
                        email: '',
                        subject: '',
                        messageContent: ''
                    });
                    setError({});
                    toast.success(trans("messages:message_sent_successfully"));
                }
            }
        } catch (e) {

        }

    }
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await axios.request({
                    baseURL: config.controllURL,
                    url: lang + "/api/contact/contact/",
                    method: "get",
                });

                setPage(response.data.page);
                // Only keep Khobar / Dammam — the page should never show the visitor's location
                setBranches((response.data.branches || []).filter(isAllowedBranch));
                setContacts(response.data.contacts);
                setSubjects(response.data.subjects.map(item => ({ text: item.description, value: item.id })));
            } catch (error) {
                console.log(error);
            }
        }
        fetchData();

        dispatch(changeIcons({ iconsBlack: true }));
        return () => {
            dispatch(changeIcons({ iconsBlack: false }));
        };
    }, [dispatch, lang]);

    useEffect(() => {
        if (branches.length > 0)
            setSelectedBranch(branches[0]);
    }, [branches]);


    const [seoPageInfo, setSeoPageInfo] = useState({});
    useEffect(() => {
        async function fetchSeo() {
            const pageName = "contact";
            try {
                const response = await axios.post(
                    `${config.controllURL}${lang}/api/Pages/SiteFetchSeoPages`,
                    {
                        "page": pageName
                    }
                );
                if (response.status === 200) {
                    setSeoPageInfo(response.data[0]);
                }
            } catch (e) {
                console.log(e);
            }
        }
        fetchSeo();
    }, []);
    return (<Fragment>


        {
            seoPageInfo &&

            <Helmet>

                <meta charSet="utf-8" />
                {
                    seoPageInfo?.title &&
                    <title>{seoPageInfo?.title}</title>
                }
                {
                    seoPageInfo?.link &&
                    <link rel="canonical" href={seoPageInfo?.link} />

                }
                {
                    seoPageInfo?.MetaDescription &&
                    <meta name="description" content={seoPageInfo?.MetaDescription} />
                }
                {
                    seoPageInfo?.MetaGroups &&
                    <meta name="keywords" content={seoPageInfo?.MetaGroups} />
                }

            </Helmet>


        }
        <div className={"mb-20 mt-xs"}>
            <MapBanner contacts={contacts} branches={branches} activebranche={selectedBranch} />
        </div>

        <section className="sec bg-bg2">
            <div className="ctn">
                <div className="container p-0">
                    <Breadcrumb className={"mb-12"}
                        links={[
                            {
                                name: trans("common:contact-us")
                            }
                        ]}
                    />
                </div>

                <div className={"grid-2-cols " + languageDir}>
                    <div>
                        <div className="stag">{trans("common:locations")}</div>
                        <h2 className="sh mb-6">{page?.name?.replace(/<br\s*\/?>/gi, ' ')}</h2>
                        <p className="text-16 text-t3 mb-10 leading-relaxed max-w-lg">{page?.content?.replace(/<br\s*\/?>/gi, ' ')}</p>

                        <div className="flex gap-3 mb-10 overflow-x-auto pb-4 scrollbar-hide">
                            {branches?.map((branch, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedBranch(branch)}
                                    className={`px-8 py-3 rounded-xl border-2 font-bold transition-all whitespace-nowrap ${branch.name === selectedBranch?.name
                                        ? "bg-g border-g text-white shadow-lg shadow-g"
                                        : "bg-surface border-secondary-light text-t3 hover:border-g"
                                        }`}
                                >
                                    {branch.content}
                                </button>
                            ))}
                        </div>

                        <div className="grid-2-cols">
                            {contacts?.map((contact, index) => {
                                if (contact.branchId === selectedBranch?.name)
                                    return (
                                        <ContactTile
                                            key={index}
                                            icon={contact.typeIconPath}
                                            value={contact.contactValue}
                                            type={contact.typeValue}
                                            name={contact.contactTitle ?? contact.typeTitle}
                                        />
                                    );
                                return null;
                            })}
                        </div>
                    </div>

                    <div className="contact-form-container">
                        <div className="stag">{trans("common:contact-info")}</div>
                        <h3>{trans("common:contact-us")}</h3>
                        <p className="form-sub">{trans("form:please-choose-another-date") ? trans("form:please-choose-another-date").split(' ')[0] : "Direct message"}</p>

                        <form
                            className={"contact-form"}
                            onSubmit={submitRequest}
                        >
                            <div className="form-group">
                                <label>{trans("form:name")}</label>
                                <InputField
                                    placeholder={trans("form:name")}
                                    name='fullName'
                                    value={message.fullName}
                                    errorLabel={error?.fullName}
                                    onChangeValue={(e) => setMessage({ ...message, fullName: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>{trans("form:phone")}</label>
                                <PhoneDropDown
                                    errorLabel={error?.phone}
                                    onChangeValue={(countryCode, phoneNumber) => {
                                        setMessage({ ...message, phone: `+${countryCode}${phoneNumber}` });
                                    }}
                                />
                            </div>

                            <div className="form-group">
                                <label>{trans("form:email")}</label>
                                <InputField
                                    placeholder={trans("form:email")}
                                    name='email'
                                    value={message.email}
                                    errorLabel={error?.email}
                                    onChangeValue={(e) => setMessage({ ...message, email: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>{trans("form:subject")}</label>
                                <DropDownInput
                                    onSelect={(e) => setMessage({ ...message, subject: String(e) })}
                                    selectedValue={message.subject}
                                    name='subject'
                                    className={"flex-grow-1"}
                                    defaultOption={{ text: trans("form:subject"), value: "0" }}
                                    options={[...subjects]}
                                    errorLabel={error?.subject}
                                />
                            </div>

                            <div className="form-group">
                                <label>{trans("form:message")}</label>
                                <TextArea
                                    value={message.messageContent}
                                    placeholder={trans("form:message")}
                                    name='messageContent'
                                    errorLabel={error?.messageContent}
                                    onChangeValue={(e) => setMessage({ ...message, messageContent: e })}
                                    row={4}
                                />
                            </div>

                            <button
                                type="submit"
                                className="bg-g text-white py-5 rounded-xl font-bold text-18 hover:bg-gd transition-all shadow-lg shadow-g mt-4"
                            >
                                {trans("form:send")}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    </Fragment>

    )
}

